import type { CragReport, CragReportStatus } from '../../types/crag-report';

/**
 * How each status is drawn, in one place: the crag page, the route's sheet and
 * the panel's queue all label the same three states, and a report that is amber
 * in one list and grey in the next stops being readable at a glance.
 *
 * Amber is deliberately the *accepted* one rather than the draft: an accepted
 * report is the one that is actually true of the rock right now. A draft is
 * paperwork, and grey is what paperwork looks like.
 */
export const REPORT_STATUS_CLASSES: Record<CragReportStatus, string> = {
  draft: 'border-transparent bg-muted text-muted-foreground',
  accepted: 'border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400',
  resolved: 'border-transparent bg-green-500/15 text-green-700 dark:text-green-400',
  // Never rendered — the API filters rejected reports out of every list — but
  // the map is total so a new status cannot silently fall through to nothing.
  rejected: 'border-transparent bg-muted text-muted-foreground line-through',
};

/** Still true of the rock: vouched for and not yet fixed. */
export function isOpenReport(report: CragReport): boolean {
  return report.status === 'accepted';
}

/**
 * Reports grouped by the route they are about, keyed by climb id. Reports about
 * the crag itself are not in here — they belong above the tabs, not on a row.
 */
export function groupReportsByClimb(reports: CragReport[]): Map<string, CragReport[]> {
  const groups = new Map<string, CragReport[]>();

  for (const report of reports) {
    const climbId = report.climb?.id;
    if (!climbId) continue;

    const group = groups.get(climbId);
    if (group) group.push(report);
    else groups.set(climbId, [report]);
  }

  return groups;
}

/** The reports about the crag itself rather than about any line on it. */
export function cragLevelReports(reports: CragReport[]): CragReport[] {
  return reports.filter((report) => !report.climb);
}
